import * as THREE from 'three/webgpu';
import type {
    SketchCircularLinePatternBuildInput,
    SketchCircularLinePatternBuildResult,
} from '../../../types/sketch/circular-pattern/SketchCircularLinePatternBuilder';
import {
    isSketchPatternedConstraint,
    SketchPatternedConstraint,
} from '../solver/constraint/relationDefinition/pattern/SketchPatternedConstraint';
import {SketchCircularDistributionCalculator} from './SketchCircularDistributionCalculator';
import {SketchCircularPatternObject} from './SketchCircularPatternObject';

/**
 * 圆周直线阵列构建器。
 *
 * 职责：
 * 1. 保留调用方提供的唯一种子线；
 * 2. 按圆周分布创建其余直线实例；
 * 3. 为整个阵列构造唯一一条未绑定的 Patterned 聚合关系；
 * 4. 创建或原位更新阵列定义及稳定实例身份。
 *
 * 本类不把对象加入场景、不绑定关系、不执行求解或历史提交。
 */
export class SketchCircularLinePatternBuilder {
    /** 圆周分布计算器。 */
    private readonly distributionCalculator = new SketchCircularDistributionCalculator();

    /**
     * 从一条种子线构建闭合圆周直线阵列。
     *
     * @param input 中心点、种子线、阵列参数与实体创建端口。
     * @returns 阵列定义、全部直线和待绑定结构关系。
     */
    public build(input: SketchCircularLinePatternBuildInput): SketchCircularLinePatternBuildResult {
        const instanceCount = input.parameters.instanceCount;
        if (!Number.isInteger(instanceCount) || instanceCount < 3) {
            throw new Error('闭合多边形圆周阵列至少需要 3 个实例。');
        }
        if (input.parameters.skippedInstanceIndexes.length > 0) {
            throw new Error('闭合多边形圆周阵列不允许跳过实例。');
        }

        const distributionItems = this.distributionCalculator.calculate({
            instanceCount,
            startAngleRad: THREE.MathUtils.degToRad(input.parameters.startAngleDeg),
            totalAngleRad: THREE.MathUtils.degToRad(input.parameters.totalAngleDeg),
            equalSpacing: input.parameters.equalSpacing,
            spacingAngleRad: THREE.MathUtils.degToRad(input.parameters.spacingAngleDeg),
            direction: input.parameters.reverseDirection ? 'clockwise' : 'counterclockwise',
            skippedInstanceIndexes: [],
        });
        if (distributionItems.length !== instanceCount) {
            throw new Error('闭合多边形圆周阵列实例分布不完整。');
        }

        const centerWorld = input.centerPoint.getPosition();
        const seedStartWorld = input.seedLine.startPoint.getPosition();
        const normal = input.seedLine.referencePlane.normal.clone().normalize();
        const canReuseExistingLines = input.existingLines?.length === instanceCount
            && input.existingLines[0] === input.seedLine
            && input.existingLines.every((line, instanceIndex) => (
                line.endPoint === input.existingLines?.[(instanceIndex + 1) % instanceCount]?.startPoint
            ));
        const lines = canReuseExistingLines ? [...input.existingLines!] : [input.seedLine];
        const vertices = canReuseExistingLines
            ? lines.map((line) => line.startPoint)
            : [input.seedLine.startPoint, input.seedLine.endPoint];

        for (let instanceIndex = 2; instanceIndex < instanceCount; instanceIndex += 1) {
            const item = distributionItems[instanceIndex]!;
            const position = seedStartWorld.clone()
                .sub(centerWorld)
                .applyAxisAngle(normal, item.rotationRad)
                .add(centerWorld);
            if (canReuseExistingLines) {
                // 参数重建优先保留实例身份，只更新共享顶点作为本次求解初值。
                vertices[instanceIndex]!.syncPositionSilently(position);
            } else {
                vertices.push(input.createPoint(position));
            }
        }

        if (canReuseExistingLines) {
            lines.forEach((line) => line.refreshGeometryOnly());
        } else {
            for (let instanceIndex = 1; instanceIndex < instanceCount; instanceIndex += 1) {
                lines.push(input.createLine(
                    vertices[instanceIndex]!,
                    vertices[(instanceIndex + 1) % instanceCount]!,
                ));
            }
        }

        const definition = {
            sourceEntityPersistentIds: [input.seedLine.persistentId],
            parameters: input.parameters,
            instances: lines.map((line, instanceIndex) => ({
                instanceIndex,
                entityPersistentIds: [line.persistentId],
            })),
        };
        const patternObject = input.existingPattern ?? new SketchCircularPatternObject(definition);
        if (input.existingPattern) {
            input.existingPattern.updateDefinition(definition);
        }

        const instances = lines.slice(1).map((_line, derivedIndex) => {
            const instanceIndex = derivedIndex + 1;
            return {
                instanceIndex,
                rotationRad: distributionItems[instanceIndex]!.rotationRad,
            };
        });
        const existingRelation = canReuseExistingLines
            ? input.seedLine.constraints.getRelations().find((relation) => {
                if (!isSketchPatternedConstraint(relation)) {
                    return false;
                }
                const parameters = relation.getParametersSnapshot();
                return parameters.patternPersistentId === patternObject.persistentId
                    && Array.isArray(parameters.instances)
                    && parameters.instances.length === instances.length
                    && parameters.instances.every((instance, instanceOffset) => (
                        instance.instanceIndex === instances[instanceOffset]!.instanceIndex
                        && Math.abs(instance.rotationRad - instances[instanceOffset]!.rotationRad) <= 1e-12
                    ));
            })
            : undefined;
        const centerLocal = input.seedLine.referencePlane.worldToLocal(centerWorld);
        const relation = existingRelation ?? new SketchPatternedConstraint({
            patternPersistentId: patternObject.persistentId,
            center2d: [centerLocal.x, centerLocal.y],
            hasCenterPointReference: true,
            seedTargetKinds: ['entity'],
            rotateInstances: true,
            instances,
        });
        const relationAttachment = {
            relation,
            targets: [input.centerPoint, input.seedLine, ...lines.slice(1)],
        };

        return {
            patternObject,
            lines,
            relationAttachment,
        };
    }
}
